
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function SleepImprovementSupport() {
  const [activeTab, setActiveTab] = useState('assessment');
  const [sleepData, setSleepData] = useState({
    bedtime: '',
    wakeTime: '',
    sleepQuality: '',
    sleepDuration: '',
    difficulties: []
  });

  const tabs = [
    { id: 'assessment', name: '睡眠評価', icon: 'ri-survey-line' },
    { id: 'hygiene', name: '睡眠衛生', icon: 'ri-shield-check-line' },
    { id: 'environment', name: '環境改善', icon: 'ri-home-line' },
    { id: 'schedule', name: '睡眠スケジュール', icon: 'ri-calendar-line' }
  ];

  const sleepHygieneTips = [
    {
      category: '就寝前の習慣',
      tips: [
        'カフェインは就寝6時間前まで',
        'アルコールは就寝3時間前まで',
        '重い食事は就寝2時間前まで',
        'ブルーライトを避ける（1時間前から）'
      ]
    },
    {
      category: '日中の過ごし方',
      tips: [
        '朝の光を浴びる（15-30分）',
        '規則正しい食事時間',
        '適度な運動（就寝3時間前まで）',
        '昼寝は15-20分以内'
      ]
    },
    {
      category: 'リラクゼーション',
      tips: [
        '深呼吸やストレッチ',
        '読書や音楽鑑賞',
        'ぬるめの入浴（38-40℃）',
        'アロマテラピー'
      ]
    }
  ];

  const environmentFactors = [
    {
      factor: '温度',
      optimal: '16-19℃',
      description: '体温が下がりやすい涼しい環境',
      icon: 'ri-temp-cold-line'
    },
    {
      factor: '湿度',
      optimal: '50-60%',
      description: '呼吸しやすい適度な湿度',
      icon: 'ri-drop-line'
    },
    {
      factor: '光',
      optimal: '暗闇',
      description: 'メラトニン分泌を促進',
      icon: 'ri-moon-line'
    },
    {
      factor: '音',
      optimal: '静寂',
      description: '40dB以下の静かな環境',
      icon: 'ri-volume-mute-line'
    },
    {
      factor: '寝具',
      optimal: '快適',
      description: '体に合ったマットレスと枕',
      icon: 'ri-hotel-bed-line'
    },
    {
      factor: '空気',
      optimal: '清浄',
      description: '換気の良い新鮮な空気',
      icon: 'ri-windy-line'
    }
  ];

  const scheduleSteps = [
    {
      step: 1,
      title: '現在の睡眠パターンを記録',
      description: '1週間の睡眠日記をつけて現状を把握',
      duration: '1週間'
    },
    {
      step: 2,
      title: '理想的な睡眠時間を設定',
      description: '7-9時間の睡眠時間を目標に設定',
      duration: '継続'
    },
    {
      step: 3,
      title: '起床時間を固定',
      description: '毎日同じ時間に起床する習慣をつける',
      duration: '2-3週間'
    },
    {
      step: 4,
      title: '就寝時間を調整',
      description: '起床時間から逆算して就寝時間を決める',
      duration: '2-3週間'
    },
    {
      step: 5,
      title: '睡眠効率を向上',
      description: 'ベッドは睡眠のためだけに使用',
      duration: '継続'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link to="/" className="text-gray-500 hover:text-gray-700 cursor-pointer">ホーム</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
              </li>
              <li>
                <Link to="/health-support" className="text-gray-500 hover:text-gray-700 cursor-pointer">健康サポート</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
              </li>
              <li className="text-gray-900 font-medium">睡眠改善サポート</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-50 to-indigo-50 py-16">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/b542e2d33da9da88d449fc2ae2f59ff2.jpg"
            alt="睡眠改善サポート"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            睡眠改善<span className="text-purple-600">サポート</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            良質な睡眠のための生活習慣改善と環境整備をトータルサポートします
          </p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-purple-50 hover:text-purple-600'
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Sleep Assessment */}
          {activeTab === 'assessment' && (
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">睡眠評価</h2>
                <p className="text-xl text-gray-600">現在の睡眠状況を評価して改善点を見つけましょう</p>
              </div>

              <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">睡眠日記</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">就寝時間</label>
                      <input
                        type="time"
                        value={sleepData.bedtime}
                        onChange={(e) => setSleepData({...sleepData, bedtime: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">起床時間</label>
                      <input
                        type="time"
                        value={sleepData.wakeTime}
                        onChange={(e) => setSleepData({...sleepData, wakeTime: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">睡眠の質</label>
                      <select
                        value={sleepData.sleepQuality}
                        onChange={(e) => setSleepData({...sleepData, sleepQuality: e.target.value})}
                        className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">選択してください</option>
                        <option value="excellent">非常に良い</option>
                        <option value="good">良い</option>
                        <option value="fair">普通</option>
                        <option value="poor">悪い</option>
                        <option value="very-poor">非常に悪い</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">睡眠時間</label>
                      <select
                        value={sleepData.sleepDuration}
                        onChange={(e) => setSleepData({...sleepData, sleepDuration: e.target.value})}
                        className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">選択してください</option>
                        <option value="less-than-5">5時間未満</option>
                        <option value="5-6">5-6時間</option>
                        <option value="6-7">6-7時間</option>
                        <option value="7-8">7-8時間</option>
                        <option value="8-9">8-9時間</option>
                        <option value="more-than-9">9時間以上</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-6">
                    <button className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 whitespace-nowrap cursor-pointer">
                      睡眠データを保存
                    </button>
                  </div>
                </div>

                <div className="mt-8 bg-purple-50 rounded-xl p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">睡眠改善のポイント</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-bold text-gray-900 mb-2">理想的な睡眠時間</h4>
                      <p className="text-gray-600 text-sm">成人：7-9時間</p>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-bold text-gray-900 mb-2">睡眠効率</h4>
                      <p className="text-gray-600 text-sm">ベッドにいる時間の85%以上</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sleep Hygiene */}
          {activeTab === 'hygiene' && (
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">睡眠衛生</h2>
                <p className="text-xl text-gray-600">良質な睡眠のための基本的な生活習慣</p>
              </div>

              <div className="grid lg:grid-cols-3 gap-8">
                {sleepHygieneTips.map((category, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{category.category}</h3>
                    <ul className="space-y-3">
                      {category.tips.map((tip, tipIndex) => (
                        <li key={tipIndex} className="flex items-start">
                          <i className="ri-check-line text-purple-600 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span className="text-gray-600">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="mt-12 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">睡眠衛生チェックリスト</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    '毎日同じ時間に就寝・起床している',
                    'カフェインの摂取時間に注意している',
                    '就寝前のスクリーン時間を制限している',
                    '寝室を睡眠に適した環境にしている',
                    '日中に適度な運動をしている',
                    'ストレス管理ができている'
                  ].map((item, index) => (
                    <label key={index} className="flex items-center bg-white rounded-lg p-4 cursor-pointer hover:bg-gray-50">
                      <input type="checkbox" className="mr-3 text-purple-600" />
                      <span className="text-gray-700">{item}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Environment */}
          {activeTab === 'environment' && (
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">睡眠環境の改善</h2>
                <p className="text-xl text-gray-600">快適な睡眠のための環境づくり</p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {environmentFactors.map((factor, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center">
                    <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className={`${factor.icon} text-2xl`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{factor.factor}</h3>
                    <div className="text-2xl font-bold text-purple-600 mb-2">{factor.optimal}</div>
                    <p className="text-gray-600 text-sm">{factor.description}</p>
                  </div>
                ))}
              </div>

              <div className="mt-12 bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">寝室環境改善のステップ</h3>
                <div className="space-y-6">
                  {[
                    { step: 1, title: '温度・湿度の調整', description: 'エアコンや加湿器を使用して最適な環境を作る' },
                    { step: 2, title: '光の管理', description: '遮光カーテンやアイマスクで暗闇を確保' },
                    { step: 3, title: '音の対策', description: '耳栓や防音対策で静かな環境を作る' },
                    { step: 4, title: '寝具の見直し', description: '体に合ったマットレスと枕を選択' },
                    { step: 5, title: '空気の質', description: '換気や空気清浄機で新鮮な空気を確保' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start">
                      <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                        {item.step}
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-1">{item.title}</h4>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Schedule */}
          {activeTab === 'schedule' && (
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">睡眠スケジュール管理</h2>
                <p className="text-xl text-gray-600">規則正しい睡眠リズムの確立</p>
              </div>

              <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">睡眠スケジュール改善ステップ</h3>
                  <div className="space-y-6">
                    {scheduleSteps.map((step, index) => (
                      <div key={index} className="flex items-start">
                        <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                          {step.step}
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h4>
                          <p className="text-gray-600 mb-2">{step.description}</p>
                          <span className="inline-block bg-purple-100 text-purple-800 text-sm px-2 py-1 rounded-full">
                            {step.duration}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">睡眠制限療法</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li>• ベッドは睡眠のためだけに使用</li>
                      <li>• 眠れない時は一度ベッドから出る</li>
                      <li>• 眠気を感じてからベッドに戻る</li>
                      <li>• 毎日同じ時間に起床</li>
                    </ul>
                  </div>
                  <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">刺激制御法</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li>• 寝室でのテレビ・スマホ使用禁止</li>
                      <li>• 20分以内に眠れない場合は退室</li>
                      <li>• リラックスできる活動をする</li>
                      <li>• 眠気が来たら再度就寝</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Related Services */}
          <div className="mt-16 bg-gray-50 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">関連サービス</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/mental-health/stress-assessment" className="bg-white rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-emotion-line text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">ストレス評価</h4>
                <p className="text-gray-600 text-sm">ストレスが睡眠に与える影響を評価</p>
              </Link>
              <Link to="/mental-health/relaxation-guidance" className="bg-white rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer">
                <div className="w-12 h-12 bg-green-100 text-green-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-leaf-line text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">リラクゼーション指導</h4>
                <p className="text-gray-600 text-sm">入眠を促すリラクゼーション技法</p>
              </Link>
              <Link to="/mental-health/specialist-collaboration" className="bg-white rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer">
                <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-links-line text-xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">専門医連携</h4>
                <p className="text-gray-600 text-sm">睡眠障害の専門医紹介</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">メンタルヘルス</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/mental-health/stress-assessment" className="hover:text-white cursor-pointer">ストレス評価</Link></li>
                <li><Link to="/mental-health/relaxation-guidance" className="hover:text-white cursor-pointer">リラクゼーション指導</Link></li>
                <li><Link to="/mental-health/sleep-improvement-support" className="hover:text-white cursor-pointer">睡眠改善サポート</Link></li>
                <li><Link to="/mental-health/specialist-collaboration" className="hover:text-white cursor-pointer">専門医連携</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">健康サポート</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">健康相談</Link></li>
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">サルコペニア予防</Link></li>
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">フレイル対策</Link></li>
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">栄養指導</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
                <li><Link to="/beast-blog" className="hover:text-white cursor-pointer">ビースト君ブログ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
